import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { dataEntryAPI } from '../../../services/adminService';
import { showSuccessToast, showErrorToast } from '../../../utils/notificationUtils';
import './DataEntryManager.css';

const DataEntryManager = ({ mode = 'species' }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [speciesList, setSpeciesList] = useState([]);
  const [loadingSpecies, setLoadingSpecies] = useState(false);
  const [speciesSearch, setSpeciesSearch] = useState('');

  const [speciesForm, setSpeciesForm] = useState({
    name_en: '',
    name_ar: '',
    is_active: false,
    show_in_encyclopedia: false,
    show_in_dropdown: true,
  });
  const [editingSpecies, setEditingSpecies] = useState(null);
  const [isSavingSpecies, setIsSavingSpecies] = useState(false);

  const [selectedSpecies, setSelectedSpecies] = useState('');
  const [breedList, setBreedList] = useState([]);
  const [loadingBreeds, setLoadingBreeds] = useState(false);
  const [breedForm, setBreedForm] = useState({
    name_en: '',
    name_ar: '',
    show_in_dropdown: true,
  });
  const [editingBreed, setEditingBreed] = useState(null);
  const [isSavingBreed, setIsSavingBreed] = useState(false);

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, type: null, item: null });

  const extractData = (res) => {
    const payload = res.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (payload?.status === 'success' && Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const fetchSpecies = useCallback(async () => {
    setLoadingSpecies(true);
    try {
      const res = await dataEntryAPI.getSpeciesList({ search: speciesSearch });
      setSpeciesList(extractData(res));
    } catch (error) {
      console.error('Error fetching species:', error);
      showErrorToast(t('errorOccurred'));
    } finally {
      setLoadingSpecies(false);
    }
  }, [speciesSearch, t]);

  const fetchBreeds = useCallback(async (speciesId) => {
    if (!speciesId) return;
    setLoadingBreeds(true);
    try {
      const res = await dataEntryAPI.getBreedsBySpecies(speciesId);
      setBreedList(extractData(res));
    } catch (error) {
      console.error('Error fetching breeds:', error);
      showErrorToast(t('errorOccurred'));
    } finally {
      setLoadingBreeds(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSpecies();
  }, [fetchSpecies]);

  useEffect(() => {
    if (mode === 'breeds' && selectedSpecies) {
      fetchBreeds(selectedSpecies);
    } else {
      setBreedList([]);
    }
  }, [mode, selectedSpecies, fetchBreeds]);

  const filteredSpecies = useMemo(() => {
    if (!speciesSearch) return speciesList;
    const lower = speciesSearch.toLowerCase();
    return speciesList.filter((s) =>
      (s.name_en || '').toLowerCase().includes(lower) ||
      (s.name_ar || '').toLowerCase().includes(lower) ||
      (s.name || '').toLowerCase().includes(lower)
    );
  }, [speciesList, speciesSearch]);

  const resetSpeciesForm = () => {
    setSpeciesForm({
      name_en: '',
      name_ar: '',
      is_active: false,
      show_in_encyclopedia: false,
      show_in_dropdown: true,
    });
    setEditingSpecies(null);
  };

  const resetBreedForm = () => {
    setBreedForm({
      name_en: '',
      name_ar: '',
      show_in_dropdown: true,
    });
    setEditingBreed(null);
  };

  const handleCreateSpecies = async (e) => {
    e.preventDefault();
    if (!speciesForm.name_en.trim()) {
      showErrorToast(t('dataEntry.speciesNameEnRequired', 'Species name (English) is required'));
      return;
    }

    setIsSavingSpecies(true);
    try {
      const payload = {
        name: speciesForm.name_en.trim(),
        name_en: speciesForm.name_en.trim(),
        name_ar: speciesForm.name_ar.trim() || speciesForm.name_en.trim(),
        display_name: speciesForm.name_en.trim(),
        is_active: Boolean(speciesForm.is_active),
        show_in_encyclopedia: Boolean(speciesForm.show_in_encyclopedia),
        show_in_dropdown: Boolean(speciesForm.show_in_dropdown),
      };
      await dataEntryAPI.createSpecies(payload);
      showSuccessToast(t('dataEntry.speciesCreated', 'Species created successfully'));
      resetSpeciesForm();
      fetchSpecies();
    } catch (error) {
      console.error('Error creating species:', error);
      const msg = error.response?.data?.message || error.message || t('errorOccurred');
      showErrorToast(msg);
    } finally {
      setIsSavingSpecies(false);
    }
  };

  const handleUpdateSpecies = async (e) => {
    e.preventDefault();
    if (!editingSpecies) return;
    if (!speciesForm.name_en.trim()) {
      showErrorToast(t('dataEntry.speciesNameEnRequired', 'Species name (English) is required'));
      return;
    }

    setIsSavingSpecies(true);
    try {
      const payload = {
        name: speciesForm.name_en.trim(),
        name_en: speciesForm.name_en.trim(),
        name_ar: speciesForm.name_ar.trim() || speciesForm.name_en.trim(),
        display_name: speciesForm.name_en.trim(),
        is_active: Boolean(speciesForm.is_active),
        show_in_encyclopedia: Boolean(speciesForm.show_in_encyclopedia),
        show_in_dropdown: Boolean(speciesForm.show_in_dropdown),
      };
      await dataEntryAPI.updateSpecies(editingSpecies.id, payload);
      showSuccessToast(t('dataEntry.speciesUpdated', 'Species updated successfully'));
      resetSpeciesForm();
      fetchSpecies();
    } catch (error) {
      console.error('Error updating species:', error);
      const msg = error.response?.data?.message || error.message || t('errorOccurred');
      showErrorToast(msg);
    } finally {
      setIsSavingSpecies(false);
    }
  };

  const handleEditSpecies = (species) => {
    setEditingSpecies(species);
    setSpeciesForm({
      name_en: species.name_en || species.name || '',
      name_ar: species.name_ar || '',
      is_active: Boolean(species.is_active),
      show_in_encyclopedia: Boolean(species.show_in_encyclopedia),
      show_in_dropdown: species.show_in_dropdown !== false,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleSpeciesFlag = async (species, field) => {
    const currentValue = species[field] !== false;
    const payload = { [field]: !currentValue };
    try {
      await dataEntryAPI.updateSpecies(species.id, payload);
      showSuccessToast(t('dataEntry.speciesUpdated', 'Species updated'));
      fetchSpecies();
    } catch (error) {
      console.error('Error toggling species flag:', error);
      const msg = error.response?.data?.message || error.message || t('errorOccurred');
      showErrorToast(msg);
    }
  };

  const handleCreateBreed = async (e) => {
    e.preventDefault();
    if (!selectedSpecies) {
      showErrorToast(t('dataEntry.selectSpeciesFirst', 'Please select a species first'));
      return;
    }
    if (!breedForm.name_en.trim()) {
      showErrorToast(t('dataEntry.breedNameEnRequired', 'Breed name (English) is required'));
      return;
    }

    setIsSavingBreed(true);
    try {
      const payload = {
        species_id: selectedSpecies,
        name: breedForm.name_en.trim(),
        name_en: breedForm.name_en.trim(),
        name_ar: breedForm.name_ar.trim() || breedForm.name_en.trim(),
        show_in_dropdown: Boolean(breedForm.show_in_dropdown),
      };
      await dataEntryAPI.createBreed(payload);
      showSuccessToast(t('dataEntry.breedCreated', 'Breed created successfully'));
      resetBreedForm();
      fetchBreeds(selectedSpecies);
    } catch (error) {
      console.error('Error creating breed:', error);
      const msg = error.response?.data?.message || error.message || t('errorOccurred');
      showErrorToast(msg);
    } finally {
      setIsSavingBreed(false);
    }
  };

  const handleUpdateBreed = async (e) => {
    e.preventDefault();
    if (!editingBreed) return;
    if (!selectedSpecies) {
      showErrorToast(t('dataEntry.selectSpeciesFirst', 'Please select a species first'));
      return;
    }
    if (!breedForm.name_en.trim()) {
      showErrorToast(t('dataEntry.breedNameEnRequired', 'Breed name (English) is required'));
      return;
    }

    setIsSavingBreed(true);
    try {
      const payload = {
        species_id: selectedSpecies,
        name: breedForm.name_en.trim(),
        name_en: breedForm.name_en.trim(),
        name_ar: breedForm.name_ar.trim() || breedForm.name_en.trim(),
        show_in_dropdown: Boolean(breedForm.show_in_dropdown),
      };
      await dataEntryAPI.updateBreed(editingBreed.id, payload);
      showSuccessToast(t('dataEntry.breedUpdated', 'Breed updated successfully'));
      resetBreedForm();
      fetchBreeds(selectedSpecies);
    } catch (error) {
      console.error('Error updating breed:', error);
      const msg = error.response?.data?.message || error.message || t('errorOccurred');
      showErrorToast(msg);
    } finally {
      setIsSavingBreed(false);
    }
  };

  const handleEditBreed = (breed) => {
    setEditingBreed(breed);
    setBreedForm({
      name_en: breed.name_en || breed.name || '',
      name_ar: breed.name_ar || '',
      show_in_dropdown: breed.show_in_dropdown !== false,
    });
    if (!selectedSpecies && String(breed.species_id) !== selectedSpecies) {
      setSelectedSpecies(String(breed.species_id));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleBreedFlag = async (breed, field) => {
    const currentValue = breed[field] !== false;
    const payload = { [field]: !currentValue };
    try {
      await dataEntryAPI.updateBreed(breed.id, payload);
      showSuccessToast(t('dataEntry.breedUpdated', 'Breed updated'));
      if (selectedSpecies) fetchBreeds(selectedSpecies);
    } catch (error) {
      console.error('Error toggling breed flag:', error);
      const msg = error.response?.data?.message || error.message || t('errorOccurred');
      showErrorToast(msg);
    }
  };

  const openDeleteModal = (type, item) => {
    setDeleteModal({ isOpen: true, type, item });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, type: null, item: null });
  };

  const handleConfirmDelete = async () => {
    const { type, item } = deleteModal;
    if (!item) return;

    try {
      if (type === 'species') {
        await dataEntryAPI.deleteSpecies(item.id);
        showSuccessToast(t('dataEntry.speciesHidden', 'Species hidden from dropdown'));
        if (selectedSpecies === String(item.id)) {
          setSelectedSpecies('');
        }
        if (editingSpecies?.id === item.id) {
          resetSpeciesForm();
        }
        fetchSpecies();
      } else if (type === 'breed') {
        await dataEntryAPI.deleteBreed(item.id);
        showSuccessToast(t('dataEntry.breedHidden', 'Breed hidden from dropdown'));
        if (editingBreed?.id === item.id) {
          resetBreedForm();
        }
        if (selectedSpecies) fetchBreeds(selectedSpecies);
      }
    } catch (error) {
      console.error('Error hiding:', error);
      const msg = error.response?.data?.message || error.message || t('errorOccurred');
      showErrorToast(msg);
    } finally {
      closeDeleteModal();
    }
  };

  const renderSpeciesMode = () => (
    <div className="data-entry-mode">
      <div className="data-entry-section">
        <h3 className="data-entry-section-title">
          {editingSpecies
            ? t('dataEntry.editSpecies', 'Edit Species')
            : t('dataEntry.addSpecies', 'Add New Species')}
        </h3>
        <form
          onSubmit={editingSpecies ? handleUpdateSpecies : handleCreateSpecies}
          className="data-entry-form"
        >
          <div className="data-entry-form-row">
            <div className="data-entry-field">
              <label>{t('dataEntry.nameEn', 'Name (English)')}</label>
              <input
                type="text"
                value={speciesForm.name_en}
                onChange={(e) => setSpeciesForm((prev) => ({ ...prev, name_en: e.target.value }))}
                placeholder={t('dataEntry.nameEnPlaceholder', 'e.g. Parrot')}
                disabled={isSavingSpecies}
              />
            </div>
            <div className="data-entry-field">
              <label>{t('dataEntry.nameAr', 'Name (Arabic)')}</label>
              <input
                type="text"
                dir="rtl"
                value={speciesForm.name_ar}
                onChange={(e) => setSpeciesForm((prev) => ({ ...prev, name_ar: e.target.value }))}
                placeholder={t('dataEntry.nameArPlaceholder', 'مثال: ببغاء')}
                disabled={isSavingSpecies}
              />
            </div>
          </div>

          <div className="data-entry-form-row data-entry-toggles">
            <label className="data-entry-toggle">
              <input
                type="checkbox"
                checked={speciesForm.is_active}
                onChange={(e) => setSpeciesForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                disabled={isSavingSpecies}
              />
              <span>{t('dataEntry.active', 'Active')}</span>
            </label>
            <label className="data-entry-toggle">
              <input
                type="checkbox"
                checked={speciesForm.show_in_encyclopedia}
                onChange={(e) => setSpeciesForm((prev) => ({ ...prev, show_in_encyclopedia: e.target.checked }))}
                disabled={isSavingSpecies}
              />
              <span>{t('dataEntry.showInEncyclopedia', 'Show in Encyclopedia')}</span>
            </label>
            <label className="data-entry-toggle">
              <input
                type="checkbox"
                checked={speciesForm.show_in_dropdown}
                onChange={(e) => setSpeciesForm((prev) => ({ ...prev, show_in_dropdown: e.target.checked }))}
                disabled={isSavingSpecies}
              />
              <span>{t('dataEntry.showInDropdown', 'Show in Dropdown')}</span>
            </label>
          </div>

          <div className="data-entry-form-row">
            <div className="data-entry-field data-entry-submit">
              <button type="submit" className="dev-admin-btn" disabled={isSavingSpecies}>
                {isSavingSpecies
                  ? t('dataEntry.saving', 'Saving...')
                  : editingSpecies
                  ? t('dataEntry.update', 'Update')
                  : t('dataEntry.add', 'Add')}
              </button>
              {editingSpecies && (
                <button
                  type="button"
                  className="dev-admin-btn secondary"
                  onClick={resetSpeciesForm}
                  disabled={isSavingSpecies}
                  style={{ marginTop: '0.5rem' }}
                >
                  {t('dataEntry.cancel', 'Cancel')}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      <div className="data-entry-section">
        <div className="data-entry-section-header">
          <h3 className="data-entry-section-title">
            {t('dataEntry.speciesList', 'Species List')}
          </h3>
          <div className="data-entry-search">
            <input
              type="text"
              value={speciesSearch}
              onChange={(e) => setSpeciesSearch(e.target.value)}
              placeholder={t('dataEntry.searchSpecies', 'Search species...')}
            />
          </div>
        </div>

        {loadingSpecies && speciesList.length === 0 ? (
          <div className="data-entry-loading">{t('dataEntry.loading', 'Loading...')}</div>
        ) : filteredSpecies.length === 0 ? (
          <div className="data-entry-empty">{t('dataEntry.noSpecies', 'No species found.')}</div>
        ) : (
          <div className="data-entry-table data-entry-table-scrollable">
            <div className="data-entry-table-row head">
              <div>{t('dataEntry.id', 'ID')}</div>
              <div>{t('dataEntry.nameEn', 'Name (English)')}</div>
              <div>{t('dataEntry.nameAr', 'Name (Arabic)')}</div>
              <div>{t('dataEntry.active', 'Active')}</div>
              <div>{t('dataEntry.encyclopedia', 'Encyclopedia')}</div>
              <div>{t('dataEntry.dropdown', 'Dropdown')}</div>
              <div></div>
            </div>
            {filteredSpecies.map((species) => (
              <div className="data-entry-table-row" key={species.id}>
                <div>{species.id}</div>
                <div>{species.name_en || species.name}</div>
                <div dir="rtl">{species.name_ar || species.name}</div>
                <div>
                  <input
                    type="checkbox"
                    checked={Boolean(species.is_active)}
                    onChange={() => toggleSpeciesFlag(species, 'is_active')}
                    aria-label={t('dataEntry.active', 'Active')}
                  />
                </div>
                <div>
                  <input
                    type="checkbox"
                    checked={Boolean(species.show_in_encyclopedia)}
                    onChange={() => toggleSpeciesFlag(species, 'show_in_encyclopedia')}
                    aria-label={t('dataEntry.showInEncyclopedia', 'Show in Encyclopedia')}
                  />
                </div>
                <div>
                  <input
                    type="checkbox"
                    checked={species.show_in_dropdown !== false}
                    onChange={() => toggleSpeciesFlag(species, 'show_in_dropdown')}
                    aria-label={t('dataEntry.showInDropdown', 'Show in Dropdown')}
                  />
                </div>
                <div className="data-entry-row-actions">
                  <button
                    type="button"
                    className="dev-admin-btn"
                    onClick={() => handleEditSpecies(species)}
                  >
                    {t('dataEntry.edit', 'Edit')}
                  </button>
                  <button
                    type="button"
                    className="dev-admin-btn danger"
                    onClick={() => openDeleteModal('species', species)}
                  >
                    {t('dataEntry.hide', 'Hide')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderBreedsMode = () => (
    <div className="data-entry-mode">
      <div className="data-entry-section">
        <h3 className="data-entry-section-title">
          {editingBreed
            ? t('dataEntry.editBreed', 'Edit Breed')
            : t('dataEntry.addBreed', 'Add New Breed')}
        </h3>
        <form
          onSubmit={editingBreed ? handleUpdateBreed : handleCreateBreed}
          className="data-entry-form"
        >
          <div className="data-entry-form-row">
            <div className="data-entry-field">
              <label>{t('dataEntry.selectSpecies', 'Species')}</label>
              <select
                value={selectedSpecies}
                onChange={(e) => setSelectedSpecies(e.target.value)}
                disabled={isSavingBreed || !!editingBreed}
              >
                <option value="">{t('dataEntry.selectSpeciesPlaceholder', 'Select a species')}</option>
                {speciesList.map((species) => (
                  <option key={species.id} value={species.id}>
                    {species.name_en || species.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="data-entry-field">
              <label>{t('dataEntry.nameEn', 'Breed Name (English)')}</label>
              <input
                type="text"
                value={breedForm.name_en}
                onChange={(e) => setBreedForm((prev) => ({ ...prev, name_en: e.target.value }))}
                placeholder={t('dataEntry.breedNameEnPlaceholder', 'e.g. African Grey')}
                disabled={isSavingBreed}
              />
            </div>
            <div className="data-entry-field">
              <label>{t('dataEntry.nameAr', 'Breed Name (Arabic)')}</label>
              <input
                type="text"
                dir="rtl"
                value={breedForm.name_ar}
                onChange={(e) => setBreedForm((prev) => ({ ...prev, name_ar: e.target.value }))}
                placeholder={t('dataEntry.breedNameArPlaceholder', 'مثال: أفريقي رمادي')}
                disabled={isSavingBreed}
              />
            </div>
            <div className="data-entry-field data-entry-toggle-field">
              <label className="data-entry-toggle">
                <input
                  type="checkbox"
                  checked={breedForm.show_in_dropdown}
                  onChange={(e) => setBreedForm((prev) => ({ ...prev, show_in_dropdown: e.target.checked }))}
                  disabled={isSavingBreed}
                />
                <span>{t('dataEntry.showInDropdown', 'Show in Dropdown')}</span>
              </label>
            </div>
            <div className="data-entry-field data-entry-submit">
              <button type="submit" className="dev-admin-btn" disabled={isSavingBreed}>
                {isSavingBreed
                  ? t('dataEntry.saving', 'Saving...')
                  : editingBreed
                  ? t('dataEntry.update', 'Update')
                  : t('dataEntry.add', 'Add')}
              </button>
              {editingBreed && (
                <button
                  type="button"
                  className="dev-admin-btn secondary"
                  onClick={resetBreedForm}
                  disabled={isSavingBreed}
                  style={{ marginTop: '0.5rem' }}
                >
                  {t('dataEntry.cancel', 'Cancel')}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      <div className="data-entry-section">
        <div className="data-entry-section-header">
          <h3 className="data-entry-section-title">
            {t('dataEntry.breedsList', 'Breeds List')}
          </h3>
          {!selectedSpecies ? (
            <div className="data-entry-search-hint">
              {t('dataEntry.selectSpeciesToViewBreeds', 'Select a species to view its breeds')}
            </div>
          ) : null}
        </div>

        {selectedSpecies ? (
          loadingBreeds ? (
            <div className="data-entry-loading">{t('dataEntry.loading', 'Loading...')}</div>
          ) : breedList.length === 0 ? (
            <div className="data-entry-empty">{t('dataEntry.noBreeds', 'No breeds found for this species.')}</div>
          ) : (
            <div className="data-entry-table data-entry-table-scrollable">
              <div className="data-entry-table-row head breed">
                <div>{t('dataEntry.id', 'ID')}</div>
                <div>{t('dataEntry.nameEn', 'Name (English)')}</div>
                <div>{t('dataEntry.nameAr', 'Name (Arabic)')}</div>
                <div>{t('dataEntry.dropdown', 'Dropdown')}</div>
                <div></div>
              </div>
              {breedList.map((breed) => (
                <div className="data-entry-table-row breed" key={breed.id}>
                  <div>{breed.id}</div>
                  <div>{breed.name_en || breed.name}</div>
                  <div dir="rtl">{breed.name_ar || breed.name}</div>
                  <div>
                    <input
                      type="checkbox"
                      checked={breed.show_in_dropdown !== false}
                      onChange={() => toggleBreedFlag(breed, 'show_in_dropdown')}
                      aria-label={t('dataEntry.showInDropdown', 'Show in Dropdown')}
                    />
                  </div>
                  <div className="data-entry-row-actions">
                    <button
                      type="button"
                      className="dev-admin-btn"
                      onClick={() => handleEditBreed(breed)}
                    >
                      {t('dataEntry.edit', 'Edit')}
                    </button>
                    <button
                      type="button"
                      className="dev-admin-btn danger"
                      onClick={() => openDeleteModal('breed', breed)}
                    >
                      {t('dataEntry.hide', 'Hide')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="data-entry-manager" dir={isRTL ? 'rtl' : 'ltr'}>
      <h2 className="data-entry-title">
        {mode === 'species'
          ? t('dataEntry.speciesTitle', 'Data Entry — Species')
          : t('dataEntry.breedsTitle', 'Data Entry — Breeds')}
      </h2>

      {mode === 'species' ? renderSpeciesMode() : renderBreedsMode()}

      {deleteModal.isOpen && (
        <div className="data-entry-modal-overlay">
          <div className="data-entry-modal">
            <h3>
              {deleteModal.type === 'species'
                ? t('dataEntry.hideSpecies', 'Hide Species from Dropdown')
                : t('dataEntry.hideBreed', 'Hide Breed from Dropdown')}
            </h3>
            <p>
              {t('dataEntry.confirmHide', 'Are you sure you want to hide')} <strong>{deleteModal.item?.name_en || deleteModal.item?.name}</strong> {t('dataEntry.fromDropdown', 'from the dropdown')}?
            </p>
            <div className="data-entry-modal-actions">
              <button type="button" className="dev-admin-btn danger" onClick={handleConfirmDelete}>
                {t('dataEntry.hide', 'Hide')}
              </button>
              <button type="button" className="dev-admin-btn" onClick={closeDeleteModal}>
                {t('dataEntry.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataEntryManager;
